/**
 * Weather functionality
 */

/**
 * Get user's location using the Geolocation API
 *
 * @return {Promise<Object>} The user's location position object
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000, // Increased timeout to 10 seconds
      maximumAge: 0,
    });
  });
};

/**
 * Build API URL with parameters
 *
 * @param {number|null} lat           Latitude
 * @param {number|null} lon           Longitude
 * @param {string|null} locationName  Location name
 * @param {string}      units         Units to use
 * @param {string}      nonce         WordPress nonce for security
 * @param {number}      cacheDuration Cache duration in minutes
 * @return {string} Complete API URL
 */
const buildApiUrl = (lat, lon, locationName, units, nonce, cacheDuration = 30) => {
  const apiUrl = `${window.wpApiSettings?.root || '/wp-json/'}bigbite-block-weather/v1/weather?`;

  const params = new URLSearchParams();

  // Add nonce for security
  params.append('_wpnonce', nonce);

  // Add weather request parameters
  if (lat && lon) {
    params.append('lat', lat);
    params.append('lon', lon);
  } else if (locationName) {
    params.append('location', encodeURIComponent(locationName));
  } else {
    throw new Error('No location provided. Please specify a location or allow location access.');
  }

  params.append('units', units);
  params.append('cache_duration', cacheDuration);
  return `${apiUrl}${params.toString()}`;
};

/**
 * Handle API response and validate data
 *
 * @param {Response} response The fetch response
 * @return {Promise<Object>} Parsed and validated data
 */
const handleApiResponse = async (response) => {
  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error(`Server error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (!response.ok) {
    const errorMessage =
      data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Invalid response format from weather API');
  }

  return data;
};

/**
 * Fetch weather data from the REST API
 *
 * @param {number|null} lat           Latitude (optional if locationName is provided)
 * @param {number|null} lon           Longitude (optional if locationName is provided)
 * @param {string|null} locationName  Location name (optional if lat/lon are provided)
 * @param {string}      units         Units to use (metric or imperial)
 * @param {number}      cacheDuration Cache duration in minutes
 * @return {Promise<Object>} Weather data
 */
export const fetchWeatherData = async (
  lat,
  lon,
  locationName,
  units = 'metric',
  cacheDuration = 30,
) => {
  try {
    // Get nonce from WordPress REST API settings
    const nonce = window.wpApiSettings?.nonce;

    if (!nonce) {
      throw new Error('Security token not available. Please refresh the page.');
    }

    const apiUrl = buildApiUrl(lat, lon, locationName, units, nonce, cacheDuration);

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleApiResponse(response);
  } catch (error) {
    // Re-throw with more user-friendly message for network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(
        'Unable to connect to weather service. Please check your internet connection.',
      );
    }
    throw error;
  }
};

/**
 * Determine location based on settings
 *
 * @param {Object} locationSettings The location settings
 * @return {Promise<Object>} Object with location data
 */
const determineLocation = async (locationSettings) => {
  const { source, manualLocation, determinedLocation } = locationSettings;

  // Check if we have a determined location from the server first
  if (determinedLocation) {
    return { locationName: determinedLocation };
  }

  // Manual location
  if (source === 'manual' && manualLocation) {
    return { locationName: manualLocation };
  }

  // Taxonomy location (would have been in determinedLocation)
  if (source === 'taxonomy') {
    throw new Error('No taxonomy location available. Please enter a location manually.');
  }

  // User location
  if (source === 'user') {
    try {
      const position = await getUserLocation();
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (error) {
      throw new Error(
        'Could not determine your location. Please allow location access or enter a location manually.',
      );
    }
  }

  return {};
};

/**
 * Initialize weather functionality
 *
 * @param {Object} store The store object from wp.interactivity
 */
export const initWeather = async (store) => {
  try {
    const { locationSettings } = store.getState();

    // Update state to show loading
    store.setState({ loading: true, error: false });

    // Determine location
    const locationData = await determineLocation(locationSettings);
    const { latitude, longitude, locationName } = locationData;

    // Fetch weather data
    const weatherData = await fetchWeatherData(
      latitude,
      longitude,
      locationName,
      locationSettings.units,
      locationSettings.cacheDuration,
    );

    // Update state with weather data
    store.setState({
      loading: false,
      error: false,
      weather: weatherData,
      location: weatherData.location,
    });
  } catch (error) {
    // Handle errors
    store.setState({
      loading: false,
      error: true,
      errorMessage: error.message || 'Failed to load weather data. Please try again.',
    });
  }
};
